import { useEffect, useState } from 'react';

import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkIconAccountCircle from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/account-circle'; // Icons to be updated in SHIBA-1069
import { useMediaQuery } from '@web-platform/shared-banana-utils-frontend/src/helpers/hooks/useMediaQuery/useMediaQuery';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { LOGIN_MODAL_REFERRERS } from '@web-platform/shared-universal-features-global-login-form';
import { loginVisibilityChanged } from '@web-platform/shared-universal-redux/src/actions/user';
import { getUserIdentifiers } from '@web-platform/shared-universal-redux/src/state';
import {
  trackChokePoint,
  trackSmartMetric,
} from '@web-platform/shared-universal-tracking/src/trackExperiment';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import { logOperationalEvent } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { getProfilePicture } from 'saddlebag-user-management';

import STYLES from './LoginButton.module.scss';

type Props = {
  buttonStyle?: string;
  iconStyle?: string;
  screenName: string;
};

type ContentProps = {
  ariaTextContent: string;
  iconStyle?: string;
  isLoggedIn: boolean | null;
  isSmallScreen: boolean;
  onPictureError: () => void;
  profilePictureUrl?: string;
};

const Content = ({
  ariaTextContent,
  iconStyle,
  isLoggedIn,
  isSmallScreen,
  onPictureError,
  profilePictureUrl,
}: ContentProps) => (
  <>
    {profilePictureUrl ? (
      <img
        data-testid="ProfilePicture"
        className={classnames(STYLES.profilePicture, iconStyle)}
        alt={ariaTextContent}
        src={profilePictureUrl}
        onError={onPictureError}
      />
    ) : (
      <>
        <BpkIconAccountCircle
          data-testid={isLoggedIn ? 'LoggedInIcon' : 'LoggedOutIcon'}
          className={classnames(STYLES.accountIcon, iconStyle)}
        />

        {!isLoggedIn && !isSmallScreen && (
          <span className={STYLES.loginText} data-testid="LogInText">
            {translate('label_header_login')}
          </span>
        )}
      </>
    )}
  </>
);

const LoginButton = ({ buttonStyle, iconStyle, screenName }: Props) => {
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const userInfo = useSelector(getUserIdentifiers);
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery(BREAKPOINTS.SMALL_TABLET);

  const PROFILE_PAGE_URL = '/profile';

  useEffect(() => {
    if (userInfo.isLoggedIn) {
      // Not updating, will be replace by the GC eventually
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProfilePictureUrl(getProfilePicture());
    }
  }, [userInfo.isLoggedIn]);

  const onPictureError = () => {
    setProfilePictureUrl('');
  };

  const ariaTextContent = userInfo.isLoggedIn
    ? translate('aria_profile_icon')
    : translate('aria_login_icon');

  const loginButtonClicked = () => {
    trackChokePoint('users_who_clicked_header_login_button');
    if (screenName === 'HOME_PAGE') {
      trackSmartMetric('homepage_login_form_open');
    }
    dispatch(loginVisibilityChanged(true, LOGIN_MODAL_REFERRERS.HEADER_LOG_IN));
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_LOGIN_CLICKED.NAME,
    });
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_LOGIN_CLICKED.NAME,
      strings: {
        screenName,
      },
    });
  };

  const profileButtonClicked = () => {
    const hasProfilePicture = profilePictureUrl?.length > 0;
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_PROFILE_CLICKED.NAME,
      strings: {
        screenName,
      },
      bools: {
        hasProfilePicture,
      },
    });
  };

  return (
    <>
      {userInfo.isLoggedIn ? (
        <a
          aria-label={ariaTextContent}
          className={buttonStyle}
          onClick={profileButtonClicked}
          href={PROFILE_PAGE_URL}
        >
          <Content
            ariaTextContent={ariaTextContent}
            iconStyle={iconStyle}
            isLoggedIn={userInfo.isLoggedIn}
            isSmallScreen={isSmallScreen}
            onPictureError={onPictureError}
            profilePictureUrl={profilePictureUrl}
          />
        </a>
      ) : (
        <button
          aria-label={ariaTextContent}
          className={buttonStyle}
          onClick={loginButtonClicked}
          type="button"
        >
          <Content
            ariaTextContent={ariaTextContent}
            iconStyle={iconStyle}
            isLoggedIn={userInfo.isLoggedIn}
            isSmallScreen={isSmallScreen}
            onPictureError={onPictureError}
            profilePictureUrl={profilePictureUrl}
          />
        </button>
      )}
    </>
  );
};

export default LoginButton;
